import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { fetchFilters } from '@api/analytics';

import { useAnalytics } from '@contexts/analyticsContext';

import {
  addRecentCustomFilter,
  getRecentCustomFilters,
} from '../_utils/localStorageHelpers';

/**
 * Modal component for selecting a custom analytics filter.
 * Displays a searchable list of saved filters and recently used filters.
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Controls the open/close state of the modal.
 * @param {function} props.onClose - Callback function to close the modal.
 * @param {function} props.onFilterSelect - Callback function when a filter is selected,
 * receives the selected filter object.
 * @param {function} props.onCreateNewFilter - Callback function when creating new filter.
 */
function CustomFilterSelectModal({
  open,
  onClose,
  onFilterSelect,
  onCreateNewFilter,
}) {
  const navigate = useNavigate();
  const { authToken, selectedProperty } = useAnalytics();

  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // State to store recently used filters from local storage
  const [recentFilters, setRecentFilters] = useState([]);

  // Fetch filters from the API and recent filters from local storage
  useEffect(() => {
    if (!open || !authToken) {
      return;
    }

    const loadFilters = async () => {
      try {
        setLoading(true);
        // Fetch all saved filters from the backend
        const allFilters = await fetchFilters({ token: authToken });
        setFilters(allFilters);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Load recent filters from local storage
    const recents = getRecentCustomFilters();
    setRecentFilters(recents);

    loadFilters();
  }, [open, authToken]); // Depend on 'open' to refetch every time the modal is opened

  // Filter the list of all filters based on the search query
  const filteredFilters = filters.filter((filter) =>
    filter.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleFilterClick = (filter) => {
    if (!selectedProperty) {
      toast.error('Please select a property first!');
      onClose();
      return;
    }

    // Add the selected filter to the list of recents
    addRecentCustomFilter(filter);
    // Update the local state for immediate feedback
    setRecentFilters((prev) => [
      { _id: filter._id, name: filter.name },
      ...prev.filter((f) => f._id !== filter._id),
    ]);

    onFilterSelect(filter);
    onClose();
  };

  const handleCreateNewFilter = () => {
    if (!selectedProperty) {
      toast.error('Please select a property first!');
      onClose();
      return;
    }

    onCreateNewFilter();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Select a Custom Filter</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            placeholder='Search filters...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Recently Used Filters Section */}
        {recentFilters.length > 0 && (
          <Box mb={2}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 'bold' }}>
              Recently Used
            </Typography>
            <List dense>
              {recentFilters.map((filter) => (
                <Paper key={filter._id} elevation={1} sx={{ mb: 1 }}>
                  <ListItem button onClick={() => handleFilterClick(filter)}>
                    <ListItemText primary={filter.name} />
                  </ListItem>
                </Paper>
              ))}
            </List>
            <Divider />
          </Box>
        )}

        {/* All Filters Section */}
        <Box>
          <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 'bold' }}>
            All Filters
          </Typography>
          {loading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color='error'>{error}</Typography>
          ) : (
            <List dense>
              {filteredFilters.length > 0 ? (
                filteredFilters.map((filter) => (
                  <ListItem
                    key={filter._id}
                    button
                    onClick={() => handleFilterClick(filter)}
                  >
                    <ListItemText primary={filter.name} />
                  </ListItem>
                ))
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No filters found.
                </Typography>
              )}
            </List>
          )}
        </Box>
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {`Can't find a filter?`}
          </Typography>
          <Button variant='contained' onClick={handleCreateNewFilter}>
            Create New Filter
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default CustomFilterSelectModal;
